/**
 * TasksView — задачи для топика kind='tasks'.
 * Календарь стилизован под Google Calendar (День/Неделя/Месяц/Список).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Icon } from "@/components/prototype/Icon";
import { Avatar } from "@/components/prototype/Avatar";
import {
  fetchTaskList,
  type Task, type TaskStatus,
} from "@/api/tasks";
import { type ChatTopic } from "@/api/dashboard";
import { useAuth } from "@/auth/AuthContext";
import { parseBackendIso } from "@/lib/presence";
import { AutomationSettingsModal } from "@/components/AutomationSettingsModal";
import { TaskCreatorModal } from "@/components/TaskCreatorModal";
import { TaskDetailModal } from "@/components/TaskDetailModal";

interface Props {
  topicId: number;
  topicName: string;
  topics?: ChatTopic[];
}

type FilterTab = "all" | "mine" | "by_me" | "burning" | "overdue" | "done";
type Period = "day" | "week" | "month" | "list";

const STATUS_COLORS: Record<TaskStatus, string> = {
  open: "#0b8043",        // Google green
  in_progress: "#1a73e8", // Google blue
  done: "#9aa0a6",        // Google grey
  cancelled: "#d93025",   // Google red
};

const WEEKDAYS_RU = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"] as const; // sunday-first for display
const WEEKDAYS_FULL_RU = ["Воскресенье", "Понедельник", "Вторник", "Среда", "Четверг", "Пятница", "Суббота"] as const;
const MONTHS_RU = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
] as const;
const MONTHS_GEN_RU = [
  "января", "февраля", "марта", "апреля", "мая", "июня",
  "июля", "августа", "сентября", "октября", "ноября", "декабря",
] as const;

const PERIOD_LABELS: Record<Period, string> = {
  day: "День",
  week: "Неделя",
  month: "Месяц",
  list: "Список",
};

const HOUR_HEIGHT = 48; // px per hour in day/week grids
const SCROLL_INTO_HOUR = 7;
const EVENT_DURATION_MIN = 60; // тасков нет «диапазона» — принимаем 1ч на блок

type LaidOutEvent = {
  task: Task;
  date: Date;
  topPx: number;
  heightPx: number;
  lane: number;
  lanesTotal: number;
};

/** Раскладывает события колонки по «полосам» (lanes). Пересекающиеся
 *  во времени события расходятся по соседним полосам. Внутри кластера
 *  пересечений все события получают одинаковый lanesTotal — чтобы делили
 *  ширину поровну. Несвязанные кластеры — независимы. */
function layoutColumnEvents(items: { task: Task; date: Date }[]): LaidOutEvent[] {
  if (items.length === 0) return [];
  const eventsSorted = [...items].sort((a, b) => a.date.getTime() - b.date.getTime());
  const eventStart = (d: Date) => d.getTime();
  const eventEnd = (d: Date) => d.getTime() + EVENT_DURATION_MIN * 60_000;

  // Шаг 1: проставить lane (минимальный индекс, в котором событие не пересекается с предыдущим)
  const laneEnds: number[] = [];
  const laneByIdx: number[] = [];
  for (let i = 0; i < eventsSorted.length; i++) {
    const s = eventStart(eventsSorted[i].date);
    const e = eventEnd(eventsSorted[i].date);
    let placed = -1;
    for (let li = 0; li < laneEnds.length; li++) {
      if (laneEnds[li] <= s) { placed = li; break; }
    }
    if (placed === -1) {
      laneEnds.push(e);
      laneByIdx[i] = laneEnds.length - 1;
    } else {
      laneEnds[placed] = e;
      laneByIdx[i] = placed;
    }
  }

  // Шаг 2: кластеры — соседние события, у которых интервалы пересекаются
  const clusters: number[][] = [];
  let cur: number[] = [];
  let curEnd = -Infinity;
  for (let i = 0; i < eventsSorted.length; i++) {
    const s = eventStart(eventsSorted[i].date);
    const e = eventEnd(eventsSorted[i].date);
    if (cur.length === 0 || s < curEnd) {
      cur.push(i);
      curEnd = Math.max(curEnd, e);
    } else {
      clusters.push(cur);
      cur = [i];
      curEnd = e;
    }
  }
  if (cur.length) clusters.push(cur);

  const out: LaidOutEvent[] = new Array(eventsSorted.length);
  for (const cluster of clusters) {
    const totalLanes = Math.max(...cluster.map(i => laneByIdx[i])) + 1;
    for (const i of cluster) {
      const d = eventsSorted[i].date;
      out[i] = {
        task: eventsSorted[i].task,
        date: d,
        topPx: (d.getHours() + d.getMinutes() / 60) * HOUR_HEIGHT,
        heightPx: (EVENT_DURATION_MIN / 60) * HOUR_HEIGHT,
        lane: laneByIdx[i],
        lanesTotal: totalLanes,
      };
    }
  }
  return out;
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function startOfWeekSun(d: Date): Date {
  // Google показывает неделю с воскресенья (по локали ru — тоже sunday-first в показанном скрине).
  const x = startOfDay(d);
  const dow = x.getDay();  // 0=Sun
  x.setDate(x.getDate() - dow);
  return x;
}
function startOfMonthGridSun(d: Date): Date {
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  return startOfWeekSun(first);
}
function gmtLabel(d: Date): string {
  const off = -d.getTimezoneOffset() / 60;
  return `GMT${off >= 0 ? "+" : ""}${off}`;
}

export function TasksView({ topicId, topicName, topics }: Props) {
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const myId = currentUser?.id;
  const isSuper = !!currentUser?.is_superuser;

  const [filter, setFilter] = useState<FilterTab>("all");
  const [period, setPeriod] = useState<Period>("month");
  const [periodMenuOpen, setPeriodMenuOpen] = useState(false);
  const [periodMenuPos, setPeriodMenuPos] = useState<{ top: number; right: number } | null>(null);
  const periodBtnRef = useRef<HTMLButtonElement | null>(null);
  const periodWrapRef = useRef<HTMLDivElement | null>(null);
  const [anchorDate, setAnchorDate] = useState<Date>(() => startOfDay(new Date()));
  const [createOpen, setCreateOpen] = useState(false);
  const [createPrefill, setCreatePrefill] = useState<Date | null>(null);
  const [selectedCellKey, setSelectedCellKey] = useState<string | null>(null);
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  const [automationOpen, setAutomationOpen] = useState(false);

  const gridScrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setFilter("all");
    setPeriod("month");
    setAnchorDate(startOfDay(new Date()));
  }, [topicId]);

  useEffect(() => {
    if (!periodMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (periodWrapRef.current && !periodWrapRef.current.contains(e.target as Node)) {
        setPeriodMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [periodMenuOpen]);

  useEffect(() => {
    if (period !== "day" && period !== "week") return;
    const el = gridScrollRef.current;
    if (!el) return;
    el.scrollTop = SCROLL_INTO_HOUR * HOUR_HEIGHT;
  }, [period, anchorDate]);

  const listQ = useQuery({
    queryKey: ["tasks/list", topicId],
    queryFn: () => fetchTaskList(topicId),
    staleTime: 15_000,
  });

  const refetch = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["tasks/list", topicId] });
  }, [qc, topicId]);

  const allTasks = listQ.data?.tasks ?? [];

  const filteredTasks = useMemo(() => {
    const nowMs = Date.now();
    const in1hMs = nowMs + 60 * 60_000;
    const isOpenWithDeadline = (t: Task) => {
      if (t.status === "done" || t.status === "cancelled") return false;
      if (!t.deadline_at) return false;
      const d = parseBackendIso(t.deadline_at);
      return !!d && !Number.isNaN(d.getTime());
    };
    const deadlineMs = (t: Task) => {
      const d = parseBackendIso(t.deadline_at!);
      return d!.getTime();
    };
    let list = allTasks;
    if (filter === "mine") list = list.filter(t => t.assignees.some(a => a.user_id === myId));
    else if (filter === "by_me") list = list.filter(t => t.creator_user_id === myId);
    else if (filter === "done") list = list.filter(t => t.status === "done");
    else if (filter === "overdue") list = list.filter(t => isOpenWithDeadline(t) && deadlineMs(t) < nowMs);
    else if (filter === "burning") list = list.filter(t => {
      if (!isOpenWithDeadline(t)) return false;
      const dl = deadlineMs(t);
      return dl >= nowMs && dl <= in1hMs;
    });
    else list = list.filter(t => t.status !== "done");
    return list;
  }, [allTasks, filter, myId]);

  type Dated = { task: Task; date: Date };
  const datedTasks = useMemo<Dated[]>(() => {
    const out: Dated[] = [];
    for (const t of filteredTasks) {
      if (!t.deadline_at) continue;
      const d = parseBackendIso(t.deadline_at);
      if (!d || Number.isNaN(d.getTime())) continue;
      out.push({ task: t, date: d });
    }
    return out;
  }, [filteredTasks]);

  const tasksByDay = useMemo(() => {
    const map = new Map<string, Dated[]>();
    for (const x of datedTasks) {
      const k = dateKey(startOfDay(x.date));
      const arr = map.get(k) ?? [];
      arr.push(x);
      map.set(k, arr);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.date.getTime() - b.date.getTime());
    return map;
  }, [datedTasks]);

  const undatedTasks = useMemo(() => filteredTasks.filter(t => !t.deadline_at), [filteredTasks]);

  const counts = useMemo(() => {
    const nowMs = Date.now();
    const in1hMs = nowMs + 60 * 60_000;
    const dlMs = (t: Task) => {
      if (!t.deadline_at) return null;
      const d = parseBackendIso(t.deadline_at);
      return d && !Number.isNaN(d.getTime()) ? d.getTime() : null;
    };
    const isOpen = (t: Task) => t.status !== "done" && t.status !== "cancelled";
    return {
      all: allTasks.filter(t => t.status !== "done").length,
      mine: allTasks.filter(t => t.status !== "done" && t.assignees.some(a => a.user_id === myId)).length,
      by_me: allTasks.filter(t => t.status !== "done" && t.creator_user_id === myId).length,
      burning: allTasks.filter(t => { if (!isOpen(t)) return false; const d = dlMs(t); return d != null && d >= nowMs && d <= in1hMs; }).length,
      overdue: allTasks.filter(t => { if (!isOpen(t)) return false; const d = dlMs(t); return d != null && d < nowMs; }).length,
      done: allTasks.filter(t => t.status === "done").length,
    };
  }, [allTasks, myId]);

  const periodTitle = useMemo(() => {
    if (period === "day") {
      return `${anchorDate.getDate()} ${MONTHS_GEN_RU[anchorDate.getMonth()]} ${anchorDate.getFullYear()}, ${WEEKDAYS_FULL_RU[anchorDate.getDay()].toLowerCase()}`;
    }
    if (period === "week") {
      const ws = startOfWeekSun(anchorDate);
      const we = addDays(ws, 6);
      if (ws.getMonth() === we.getMonth()) {
        return `${ws.getDate()}–${we.getDate()} ${MONTHS_GEN_RU[ws.getMonth()]} ${ws.getFullYear()}`;
      }
      return `${ws.getDate()} ${MONTHS_GEN_RU[ws.getMonth()]} – ${we.getDate()} ${MONTHS_GEN_RU[we.getMonth()]} ${we.getFullYear()}`;
    }
    if (period === "month") {
      return `${MONTHS_RU[anchorDate.getMonth()]} ${anchorDate.getFullYear()}`;
    }
    return "Все задачи";
  }, [period, anchorDate]);

  const monthCells = useMemo(() => {
    const start = startOfMonthGridSun(anchorDate);
    return Array.from({ length: 42 }, (_, i) => addDays(start, i));
  }, [anchorDate]);

  const weekCells = useMemo(() => {
    const ws = startOfWeekSun(anchorDate);
    return Array.from({ length: 7 }, (_, i) => addDays(ws, i));
  }, [anchorDate]);

  const today = startOfDay(new Date());
  const tzLabel = useMemo(() => gmtLabel(new Date()), []);

  const goPrev = () => {
    setAnchorDate(d => {
      if (period === "day") return addDays(d, -1);
      if (period === "week") return addDays(d, -7);
      return new Date(d.getFullYear(), d.getMonth() - 1, 1);
    });
  };
  const goNext = () => {
    setAnchorDate(d => {
      if (period === "day") return addDays(d, 1);
      if (period === "week") return addDays(d, 7);
      return new Date(d.getFullYear(), d.getMonth() + 1, 1);
    });
  };
  const goToday = () => setAnchorDate(startOfDay(new Date()));

  const openDayView = (d: Date) => {
    setAnchorDate(startOfDay(d));
    setPeriod("day");
  };

  const openCreatorAt = (d: Date) => {
    setCreatePrefill(d);
    setCreateOpen(true);
  };

  const togglePeriodMenu = () => {
    if (periodMenuOpen) { setPeriodMenuOpen(false); return; }
    const r = periodBtnRef.current?.getBoundingClientRect();
    if (r) setPeriodMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right });
    setPeriodMenuOpen(true);
  };

  const choosePeriod = (p: Period) => {
    setPeriod(p);
    setPeriodMenuOpen(false);
  };

  return (
    <div className="tasks-view">
      <div className="tasks-header">
        <div className="tasks-title">
          <Icon name="folder" size={20}/>
          <span>{topicName}</span>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <div className="tasks-period-wrap" ref={periodWrapRef}>
            <button
              ref={periodBtnRef}
              type="button"
              className="tasks-period-btn"
              onClick={togglePeriodMenu}
            >
              {PERIOD_LABELS[period]} <Icon name="chevron_d" size={12}/>
            </button>
            {periodMenuOpen && periodMenuPos && (
              <div
                className="tasks-period-menu"
                style={{ position: "fixed", top: periodMenuPos.top, right: periodMenuPos.right }}
              >
                {(["day","week","month","list"] as Period[]).map(p => (
                  <button
                    key={p}
                    type="button"
                    className={`tasks-period-item${period===p ? " is-active" : ""}`}
                    onClick={() => choosePeriod(p)}
                  >
                    {PERIOD_LABELS[p]}
                  </button>
                ))}
              </div>
            )}
          </div>
          {isSuper && (
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => setAutomationOpen(true)}
              title="Автоматизация"
              aria-label="Автоматизация"
              style={{ padding: "6px 8px", display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 32 }}
            >
              <Icon name="settings" size={14}/>
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setCreateOpen(true)}
            title="Новая задача"
            aria-label="Новая задача"
            style={{ padding: "6px 8px", display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 32 }}
          >
            <Icon name="plus" size={14}/>
          </button>
        </div>
      </div>

      <div className="tasks-tabs">
        {(["all", "mine", "by_me", "burning", "overdue", "done"] as FilterTab[]).map(t => (
          <button
            key={t}
            type="button"
            className={`tasks-tab tasks-tab-${t}${filter === t ? " is-active" : ""}`}
            onClick={() => setFilter(t)}
          >
            {t === "all" && "Все"}
            {t === "mine" && "Мои"}
            {t === "by_me" && "Поставил"}
            {t === "burning" && "🔥 Горящие"}
            {t === "overdue" && "🚨 Просроченные"}
            {t === "done" && "Завершённые"}
            <span className="tasks-tab-count">{counts[t]}</span>
          </button>
        ))}
      </div>

      <div className="tasks-content">
        {listQ.isLoading && <div className="tasks-loading">Загрузка…</div>}
        {listQ.error && <div className="tasks-error">Ошибка: {(listQ.error as Error).message}</div>}

        {!listQ.isLoading && period !== "list" && (
          <div className="tasks-gcal">
            <div className="tasks-gcal-toolbar">
              <button type="button" className="tasks-gcal-today-btn" onClick={goToday}>Сегодня</button>
              <button type="button" className="tasks-gcal-nav" onClick={goPrev} aria-label="Назад"><Icon name="chevron_l" size={18}/></button>
              <button type="button" className="tasks-gcal-nav" onClick={goNext} aria-label="Вперёд"><Icon name="chevron_r" size={18}/></button>
              <div className="tasks-gcal-title">{periodTitle}</div>
            </div>

            {period === "month" && (
              <MonthView
                cells={monthCells}
                anchorMonth={anchorDate.getMonth()}
                today={today}
                tasksByDay={tasksByDay}
                selectedCellKey={selectedCellKey}
                onSelectCell={setSelectedCellKey}
                onPickDate={openDayView}
                onPickTask={(t) => setDetailTask(t)}
              />
            )}

            {(period === "day" || period === "week") && (
              <DayWeekView
                cols={period === "day" ? [anchorDate] : weekCells}
                tasksByDay={tasksByDay}
                today={today}
                tzLabel={tzLabel}
                scrollRef={gridScrollRef}
                selectedCellKey={selectedCellKey}
                onSelectCell={setSelectedCellKey}
                onPickDate={openDayView}
                onPickTask={(t) => setDetailTask(t)}
                onCreateAt={openCreatorAt}
                isWeek={period === "week"}
              />
            )}

            {undatedTasks.length > 0 && period !== "week" && (
              <div className="tasks-undated">
                <div className="tasks-undated-head">Без срока · {undatedTasks.length}</div>
                {undatedTasks.map(t => <TaskRow key={t.id} task={t} onClick={() => setDetailTask(t)}/>)}
              </div>
            )}
          </div>
        )}

        {!listQ.isLoading && period === "list" && (
          <div className="tasks-list">
            {filteredTasks.length === 0 && (
              <div className="tasks-empty">
                <Icon name="folder" size={48}/>
                <div className="tasks-empty-title">Задач нет</div>
                <div className="tasks-empty-sub">Создайте первую через «Новая задача».</div>
              </div>
            )}
            {filteredTasks.map(t => <TaskRow key={t.id} task={t} onClick={() => setDetailTask(t)}/>)}
          </div>
        )}
      </div>

      {createOpen && (
        <TaskCreatorModal
          topics={topics ?? []}
          sourceTopic={(topics ?? []).find(x => x.id === topicId) ?? null}
          initialDeadline={createPrefill}
          onClose={() => { setCreateOpen(false); setCreatePrefill(null); }}
          onCreated={() => { setCreateOpen(false); setCreatePrefill(null); refetch(); }}
        />
      )}
      {editTask && (
        <TaskCreatorModal
          topics={topics ?? []}
          sourceTopic={(topics ?? []).find(x => x.id === topicId) ?? null}
          onClose={() => setEditTask(null)}
          onCreated={() => { setEditTask(null); refetch(); }}
        />
      )}
      {detailTask && (
        <TaskDetailModal task={detailTask} onClose={() => setDetailTask(null)} onChanged={refetch}/>
      )}
      {automationOpen && (
        <AutomationSettingsModal canEdit={isSuper} onClose={() => setAutomationOpen(false)}/>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// MonthView — Google Calendar style
// ─────────────────────────────────────────────────────────
function MonthView({
  cells, anchorMonth, today, tasksByDay, selectedCellKey, onSelectCell, onPickDate, onPickTask,
}: {
  cells: Date[];
  anchorMonth: number;
  today: Date;
  tasksByDay: Map<string, { task: Task; date: Date }[]>;
  selectedCellKey: string | null;
  onSelectCell: (key: string | null) => void;
  onPickDate: (d: Date) => void;
  onPickTask: (t: Task) => void;
}) {
  return (
    <div className="tasks-gcal-month-wrap">
      <div className="tasks-gcal-month-weekrow">
        {WEEKDAYS_RU.map(w => (
          <div key={w} className="tasks-gcal-month-wday-cell">{w.toUpperCase()}</div>
        ))}
      </div>
      <div className="tasks-gcal-month-grid">
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === anchorMonth;
          const isToday = sameDay(d, today);
          const isFirstOfMonth = d.getDate() === 1;
          const here = tasksByDay.get(dateKey(d)) ?? [];
          const MAX = 3;
          const vis = here.slice(0, MAX);
          const more = here.length - MAX;
          const cellKey = dateKey(d);
          const isSelected = selectedCellKey === cellKey;
          return (
            <div
              key={i}
              className={`tasks-gcal-month-cell${inMonth ? "" : " is-other-month"}${isToday ? " is-today" : ""}${isSelected ? " is-selected" : ""}`}
              onClick={(e) => {
                if ((e.target as HTMLElement).closest(".tasks-gcal-month-event")) return;
                if ((e.target as HTMLElement).closest(".tasks-gcal-month-event-more")) return;
                onSelectCell(cellKey);
              }}
              onDoubleClick={(e) => {
                if ((e.target as HTMLElement).closest(".tasks-gcal-month-event")) return;
                if ((e.target as HTMLElement).closest(".tasks-gcal-month-event-more")) return;
                onPickDate(d);
              }}
              role="button"
            >
              <div className="tasks-gcal-month-cell-head">
                <span className="tasks-gcal-month-daynum">
                  {isFirstOfMonth ? `${d.getDate()} ${MONTHS_GEN_RU[d.getMonth()].slice(0,3)}` : d.getDate()}
                </span>
              </div>
              <div className="tasks-gcal-month-events">
                {vis.map(({ task }) => (
                  <button
                    key={task.id}
                    type="button"
                    className={`tasks-gcal-month-event status-${task.status}`}
                    style={{ background: STATUS_COLORS[task.status] }}
                    onClick={(e) => { e.stopPropagation(); onPickTask(task); }}
                    title={task.title}
                  >
                    {task.title}
                  </button>
                ))}
                {more > 0 && (
                  <button
                    type="button"
                    className="tasks-gcal-month-event-more"
                    onClick={(e) => { e.stopPropagation(); onPickDate(d); }}
                  >
                    Ещё {more}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// DayWeekView — Google Calendar style (day = 1 col, week = 7 cols)
// ─────────────────────────────────────────────────────────
function DayWeekView({
  cols, tasksByDay, today, tzLabel, scrollRef, selectedCellKey, onSelectCell, onPickDate, onPickTask, onCreateAt, isWeek,
}: {
  cols: Date[];
  tasksByDay: Map<string, { task: Task; date: Date }[]>;
  today: Date;
  tzLabel: string;
  scrollRef: React.RefObject<HTMLDivElement>;
  selectedCellKey: string | null;
  onSelectCell: (key: string | null) => void;
  onPickDate: (d: Date) => void;
  onPickTask: (t: Task) => void;
  onCreateAt: (d: Date) => void;
  isWeek: boolean;
}) {
  const colsTemplate = `repeat(${cols.length}, 1fr)`;
  const bodyHeight = 24 * HOUR_HEIGHT;

  return (
    <div className="tasks-gcal-dw">
      <div className="tasks-gcal-dw-head">
        <div className="tasks-gcal-dw-tz">{tzLabel}</div>
        <div className="tasks-gcal-dw-days" style={{ gridTemplateColumns: colsTemplate }}>
          {cols.map((d, i) => {
            const isToday = sameDay(d, today);
            return (
              <button
                key={i}
                type="button"
                className={`tasks-gcal-dw-dayhead${isToday ? " is-today" : ""}`}
                onClick={() => isWeek && onPickDate(d)}
                disabled={!isWeek}
              >
                <span className="tasks-gcal-dw-weekday">{WEEKDAYS_RU[d.getDay()].toUpperCase()}</span>
                <span className="tasks-gcal-dw-daynum">{d.getDate()}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="tasks-gcal-dw-scroll" ref={scrollRef}>
        <div className="tasks-gcal-dw-body">
          <div className="tasks-gcal-dw-gutter" style={{ height: bodyHeight }}>
            {Array.from({ length: 23 }, (_, i) => {
              const h = i + 1;
              return (
                <span
                  key={h}
                  className="tasks-gcal-dw-hour-label"
                  style={{ top: h * HOUR_HEIGHT }}
                >
                  {String(h).padStart(2, "0")}:00
                </span>
              );
            })}
          </div>
          <div className="tasks-gcal-dw-cols" style={{ gridTemplateColumns: colsTemplate, height: bodyHeight }}>
            {cols.map((d, ci) => {
              const here = tasksByDay.get(dateKey(d)) ?? [];
              const isToday = sameDay(d, today);
              return (
                <div key={ci} className={`tasks-gcal-dw-col${isToday ? " is-today" : ""}`}>
                  {Array.from({ length: 24 }, (_, i) => {
                    const cellKey = `${dateKey(d)}-${i}`;
                    const isSelected = selectedCellKey === cellKey;
                    return (
                      <div
                        key={i}
                        className={`tasks-gcal-dw-cell${isSelected ? " is-selected" : ""}`}
                        style={{ height: HOUR_HEIGHT }}
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest(".tasks-gcal-dw-event")) return;
                          onSelectCell(cellKey);
                        }}
                        onDoubleClick={(e) => {
                          if ((e.target as HTMLElement).closest(".tasks-gcal-dw-event")) return;
                          if (isWeek) {
                            onPickDate(d);
                          } else {
                            const at = new Date(d);
                            at.setHours(i, 0, 0, 0);
                            onCreateAt(at);
                          }
                        }}
                      />
                    );
                  })}
                  {layoutColumnEvents(here).map(({ task, date, topPx, heightPx, lane, lanesTotal }) => {
                    const widthPct = 100 / lanesTotal;
                    const leftPct = lane * widthPct;
                    return (
                      <button
                        key={task.id}
                        type="button"
                        className={`tasks-gcal-dw-event status-${task.status}`}
                        style={{
                          top: topPx,
                          height: heightPx - 2,
                          left: `calc(${leftPct}% + 2px)`,
                          width: `calc(${widthPct}% - 4px)`,
                          right: "auto",
                          background: STATUS_COLORS[task.status],
                        }}
                        onClick={() => onPickTask(task)}
                        title={task.title}
                      >
                        <span className="tasks-gcal-dw-event-time">
                          {String(date.getHours()).padStart(2,"0")}:{String(date.getMinutes()).padStart(2,"0")}
                        </span>
                        <span className="tasks-gcal-dw-event-text">{task.title}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// TaskRow — list row
// ─────────────────────────────────────────────────────────
function TaskRow({ task, onClick }: { task: Task; onClick: () => void }) {
  const isDone = task.status === "done";
  const deadlineDate = task.deadline_at ? parseBackendIso(task.deadline_at) : null;
  const isOverdue = deadlineDate && !isDone && deadlineDate.getTime() < Date.now();
  const deadlineLabel = deadlineDate
    ? deadlineDate.toLocaleString("ru-RU", { hour: "2-digit", minute: "2-digit" })
    : null;
  return (
    <button
      type="button"
      className={`tasks-row${isDone ? " is-done" : ""}${isOverdue ? " is-overdue" : ""}`}
      onClick={onClick}
    >
      <span className="tasks-row-dot" style={{ background: STATUS_COLORS[task.status] }}/>
      <span className="tasks-row-title">{task.title}</span>
      {task.assignees.length > 0 && (
        <span className="tasks-row-assignees">
          {task.assignees.slice(0, 3).map(a => (
            <Avatar
              key={a.user_id}
              name={a.display_name ?? "?"}
              userId={a.user_id}
              avatarFilename={a.avatar_filename ?? null}
              size={20}
              color="#4fae4e"
            />
          ))}
          {task.assignees.length > 3 && (
            <span className="tasks-row-more">+{task.assignees.length - 3}</span>
          )}
        </span>
      )}
      {deadlineLabel && (
        <span className={`tasks-row-time${isOverdue ? " is-overdue" : ""}`}>{deadlineLabel}</span>
      )}
    </button>
  );
}
